import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import API from '../utils/api';

const V1 = '#7F00FF', V2 = '#B163FF', V3 = '#51158C', V4 = '#2B0057';

const ArrowLeftIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
  </svg>
);
const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const EditIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const TrashIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);
const CheckIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const LockIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const UnlockIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 9.9-1" />
  </svg>
);

const CircuitBg = () => (
  <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 0, opacity: 0.07 }}>
    <svg viewBox="0 0 1400 800" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ width: '100%', height: '100%' }}>
      <g stroke={V2} strokeWidth="0.9" fill="none">
        <line x1="0" y1="160" x2="320" y2="160" /><line x1="0" y1="420" x2="210" y2="420" />
        <circle cx="320" cy="160" r="5" fill={V1} opacity="0.9" /><circle cx="210" cy="420" r="4" fill={V2} opacity="0.9" />
      </g>
    </svg>
  </div>
);

const DURATION_OPTIONS = [
  { label: '15 Days',  value: '15 Days',  filter: '15days'  },
  { label: '1 Month',  value: '1 Month',  filter: '1month'  },
  { label: '2 Months', value: '2 Months', filter: '2month'  },
  { label: '3 Months', value: '3 Months', filter: '3month'  },
  { label: 'Other (type manually)', value: 'other', filter: '' },
];

const emptyForm = { title: '', description: '', category: '', duration: '', durationSelect: '', fee: '', level: 'Beginner', type: 'course', durationFilter: '', instructor: '', syllabus: '' };

const inputStyle = {
  width: '100%', background: 'var(--bg2)', border: '1.5px solid var(--border)',
  borderRadius: 10, color: 'var(--text)', fontFamily: "'Satoshi','DM Sans',sans-serif",
  fontSize: 14, padding: '11px 14px', outline: 'none', transition: 'border-color 0.2s, box-shadow 0.2s',
};
const onFocus = e => { e.target.style.borderColor = `${V1}88`; e.target.style.boxShadow = `0 0 0 3px ${V1}22`; };
const onBlur  = e => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; };

const AdminCourses = () => {
  const [courses, setCourses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchCourses = () => API.get('/courses/admin/all').then(r => setCourses(r.data));
  useEffect(() => { fetchCourses(); }, []);

  const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleDurationSelect = e => {
    const selected = DURATION_OPTIONS.find(o => o.value === e.target.value);
    if (!selected) return;
    if (selected.value === 'other') {
      setForm(p => ({ ...p, durationSelect: 'other', duration: '', durationFilter: '' }));
    } else {
      setForm(p => ({ ...p, durationSelect: selected.value, duration: selected.value, durationFilter: selected.filter }));
    }
  };

  const handleSubmit = async e => {
    e.preventDefault(); setLoading(true);
    const { durationSelect, ...rest } = form;
    const payload = { ...rest, fee: Number(form.fee), syllabus: form.syllabus.split('\n').filter(Boolean) };
    try {
      if (editId) { await API.put(`/courses/${editId}`, payload); toast.success('Course updated!'); }
      else        { await API.post('/courses', payload);          toast.success('Course created!'); }
      setShowForm(false); setForm(emptyForm); setEditId(null);
      fetchCourses();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  const handleEdit = c => {
    const matchedOption = DURATION_OPTIONS.find(o => o.value === c.duration);
    const durationSelect = matchedOption ? matchedOption.value : (c.duration ? 'other' : '');
    setForm({
      title:          c.title          || '',
      description:    c.description    || '',
      category:       c.category       || '',
      duration:       c.duration       || '',
      durationSelect: durationSelect,
      fee:            c.fee?.toString() || '',
      level:          c.level          || 'Beginner',
      type:           c.type           || 'course',
      durationFilter: c.durationFilter || '',
      instructor:     c.instructor     || '',
      syllabus:       (c.syllabus || []).join('\n'),
    });
    setEditId(c._id); setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this course?')) return;
    await API.delete(`/courses/${id}`); toast.success('Deleted'); fetchCourses();
  };

  const handleToggleActive = async c => {
    await API.put(`/courses/${c._id}`, { isActive: !c.isActive });
    toast.success(c.isActive ? 'Locked — hidden from the public site' : 'Unlocked — visible on the public site');
    fetchCourses();
  };

  const fieldLabel = label => (
    <label style={{ display: 'block', fontSize: 11.5, color: 'var(--muted)', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 7 }}>{label}</label>
  );

  return (
    <div style={{ background: 'var(--bg)', color: 'var(--text)', minHeight: '100vh' }}>

      {/* HEADER */}
      <section style={{ padding: '120px 0 60px', position: 'relative', overflow: 'hidden', background: 'var(--bg)' }}>
        <CircuitBg />
        <div style={{ position: 'absolute', bottom: 0, right: 0, width: 300, height: 300, borderRadius: '50%', background: `radial-gradient(circle, var(--hero-orb) 0%, transparent 70%)`, pointerEvents: 'none' }} />
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <Link to="/admin" style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            color: 'var(--muted)', fontSize: 13.5, fontWeight: 600, marginBottom: 24, transition: 'color 0.2s',
          }}
            onMouseEnter={e => e.currentTarget.style.color = V2}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--muted)'}
          >
            <ArrowLeftIcon /> Admin Dashboard
          </Link>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <div className="section-label">ADMIN PANEL</div>
              <h1 style={{ fontSize: 'clamp(26px,4vw,44px)', fontWeight: 800, letterSpacing: '-0.025em', fontFamily: "'Inter',sans-serif" }}>
                Manage <span style={{ background: `linear-gradient(135deg,${V1},${V2})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Courses</span>
              </h1>
            </div>
            <button
              onClick={() => { setShowForm(!showForm); setEditId(null); setForm(emptyForm); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '12px 22px', borderRadius: 12, border: 'none', cursor: 'pointer',
                background: showForm ? 'var(--card-solid)' : `linear-gradient(135deg,${V1},${V3})`,
                color: showForm ? 'var(--muted)' : '#fff',
                border: showForm ? '1.5px solid var(--border)' : 'none',
                fontFamily: "'Satoshi','DM Sans',sans-serif", fontWeight: 800, fontSize: 14,
                boxShadow: showForm ? 'none' : `0 4px 20px ${V1}44`,
                transition: 'all 0.2s',
              }}
            >
              {showForm ? <><XIcon /> Cancel</> : <><PlusIcon /> Add Course</>}
            </button>
          </div>
        </div>
      </section>

      {/* MAIN */}
      <section style={{ padding: '0 0 100px', background: 'var(--bg2)', borderTop: '1px solid var(--border)' }}>
        <div className="container" style={{ paddingTop: 40 }}>

          {/* Course Form */}
          {showForm && (
            <form onSubmit={handleSubmit} style={{
              background: 'var(--card-solid)', border: `1px solid ${V1}44`,
              borderRadius: 20, padding: '32px', marginBottom: 40,
              boxShadow: `0 8px 40px ${V1}15`,
            }}>
              <h3 style={{ fontSize: 18, fontWeight: 800, marginBottom: 28, fontFamily: "'Inter',sans-serif" }}>
                {editId ? 'Edit Course' : 'Add New Course'}
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 4 }} className="form-grid">
                {[
                  { name: 'title',      label: 'Course Title *',   type: 'text',   req: true,  placeholder: '' },
                  { name: 'instructor', label: 'Instructor',       type: 'text',   req: false, placeholder: 'Instructor name' },
                  { name: 'fee',        label: 'Fee (₹) *',        type: 'number', req: true,  placeholder: '' },
                ].map(({ name, label, type, req, placeholder }) => (
                  <div key={name} style={{ marginBottom: 16 }}>
                    {fieldLabel(label)}
                    <input name={name} type={type} value={form[name]} onChange={handleChange} required={req} placeholder={placeholder}
                      style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
                  </div>
                ))}

                {/* Duration — dropdown + optional manual input */}
                <div style={{ marginBottom: 16 }}>
                  {fieldLabel('Duration *')}
                  <select name="durationSelect" value={form.durationSelect} onChange={handleDurationSelect} required
                    style={{ ...inputStyle, cursor: 'pointer' }} onFocus={onFocus} onBlur={onBlur}>
                    <option value="">Select duration</option>
                    {DURATION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  {form.durationSelect === 'other' && (
                    <input
                      name="duration"
                      type="text"
                      value={form.duration}
                      onChange={handleChange}
                      required
                      placeholder="e.g. 6 Weeks, 45 Days…"
                      style={{ ...inputStyle, marginTop: 8 }}
                      onFocus={onFocus}
                      onBlur={onBlur}
                    />
                  )}
                </div>

                <div style={{ marginBottom: 16 }}>
                  {fieldLabel('Category *')}
                  <select name="category" value={form.category} onChange={handleChange} required style={{ ...inputStyle, cursor: 'pointer' }} onFocus={onFocus} onBlur={onBlur}>
                    <option value="">Select category</option>
                    {['Web Development', 'Data Science', 'Frontend', 'Backend', 'DevOps', 'Mobile', 'AI/ML', 'Cybersecurity'].map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>

                <div style={{ marginBottom: 16 }}>
                  {fieldLabel('Level')}
                  <select name="level" value={form.level} onChange={handleChange} style={{ ...inputStyle, cursor: 'pointer' }} onFocus={onFocus} onBlur={onBlur}>
                    {['Beginner', 'Intermediate', 'Advanced'].map(l => <option key={l}>{l}</option>)}
                  </select>
                </div>

                <div style={{ marginBottom: 16 }}>
                  {fieldLabel('Type *')}
                  <select name="type" value={form.type} onChange={handleChange} required style={{ ...inputStyle, cursor: 'pointer' }} onFocus={onFocus} onBlur={onBlur}>
                    <option value="course">Course</option>
                    <option value="internship">Internship</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                {fieldLabel('Description *')}
                <textarea name="description" value={form.description} onChange={handleChange} rows={3} required
                  style={{ ...inputStyle, resize: 'vertical', minHeight: 90 }} onFocus={onFocus} onBlur={onBlur} />
              </div>

              <div style={{ marginBottom: 24 }}>
                {fieldLabel('Syllabus (one item per line)')}
                <textarea name="syllabus" value={form.syllabus} onChange={handleChange} rows={5}
                  placeholder={"HTML & CSS\nJavaScript\nReact.js"}
                  style={{ ...inputStyle, resize: 'vertical', minHeight: 120 }} onFocus={onFocus} onBlur={onBlur} />
              </div>

              <button type="submit" disabled={loading} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '13px 28px', borderRadius: 12, border: 'none', cursor: loading ? 'wait' : 'pointer',
                background: `linear-gradient(135deg,${V1},${V3})`,
                color: '#fff', fontWeight: 800, fontSize: 15,
                fontFamily: "'Satoshi','DM Sans',sans-serif",
                boxShadow: `0 4px 20px ${V1}44`,
                opacity: loading ? 0.7 : 1, transition: 'opacity 0.2s',
              }}>
                <CheckIcon />
                {loading ? 'Saving...' : editId ? 'Update Course' : 'Create Course'}
              </button>
            </form>
          )}

          {/* Courses Table */}
          <div style={{ background: 'var(--card-solid)', border: '1px solid var(--border)', borderRadius: 20, overflow: 'hidden', boxShadow: 'var(--shadow-card)' }}>
            {courses.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '80px 24px', color: 'var(--muted)', fontSize: 15 }}>No courses yet. Add your first course!</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg3)' }}>
                      {['Title', 'Type', 'Category', 'Duration', 'Fee', 'Level', 'Status', 'Actions'].map(h => (
                        <th key={h} style={{
                          padding: '14px 18px', textAlign: 'left',
                          color: 'var(--muted)', fontSize: 11.5, fontWeight: 800,
                          textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap',
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {courses.map((c, i) => (
                      <tr key={c._id}
                        style={{ borderBottom: i < courses.length - 1 ? '1px solid var(--border)' : 'none', transition: 'background 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}
                      >
                        <td style={{ padding: '15px 18px', fontWeight: 700, fontSize: 14, color: 'var(--text)', maxWidth: 180 }}>{c.title}</td>
                        <td style={{ padding: '15px 18px' }}>
                          <span style={{
                            display: 'inline-block', padding: '3px 12px', borderRadius: 999, fontSize: 11.5, fontWeight: 800,
                            background: c.type === 'internship' ? 'rgba(245,166,35,0.12)' : `${V1}18`,
                            color: c.type === 'internship' ? 'var(--warning)' : V2,
                            border: `1px solid ${c.type === 'internship' ? 'rgba(245,166,35,0.25)' : `${V1}33`}`,
                            textTransform: 'uppercase', letterSpacing: '0.05em',
                          }}>
                            {c.type === 'internship' ? 'Internship' : 'Course'}
                          </span>
                          {c.durationFilter && (
                            <div style={{ color: 'var(--muted)', fontSize: 11, marginTop: 4 }}>{c.durationFilter}</div>
                          )}
                        </td>
                        <td style={{ padding: '15px 18px', color: 'var(--muted)', fontSize: 13 }}>{c.category}</td>
                        <td style={{ padding: '15px 18px', color: 'var(--muted)', fontSize: 13 }}>{c.duration}</td>
                        <td style={{ padding: '15px 18px', color: V2, fontWeight: 700, fontSize: 14 }}>₹{c.fee?.toLocaleString()}</td>
                        <td style={{ padding: '15px 18px', color: 'var(--text-2)', fontSize: 13 }}>{c.level}</td>
                        <td style={{ padding: '15px 18px' }}>
                          <span style={{
                            display: 'inline-block', padding: '3px 12px', borderRadius: 999, fontSize: 11.5, fontWeight: 800,
                            background: c.isActive ? 'rgba(46,204,113,0.12)' : 'rgba(240,65,85,0.1)',
                            color: c.isActive ? '#2ECC71' : 'var(--danger)',
                            border: `1px solid ${c.isActive ? 'rgba(46,204,113,0.28)' : 'rgba(240,65,85,0.28)'}`,
                            textTransform: 'uppercase', letterSpacing: '0.05em',
                          }}>
                            {c.isActive ? 'Active' : 'Locked'}
                          </span>
                        </td>
                        <td style={{ padding: '15px 18px' }}>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => handleToggleActive(c)} style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              padding: '7px 13px', borderRadius: 8,
                              border: c.isActive ? '1.5px solid rgba(245,166,35,0.35)' : '1.5px solid rgba(46,204,113,0.35)',
                              background: c.isActive ? 'rgba(245,166,35,0.08)' : 'rgba(46,204,113,0.08)',
                              color: c.isActive ? 'var(--warning)' : '#2ECC71',
                              fontSize: 13, fontWeight: 700, cursor: 'pointer',
                              fontFamily: "'Satoshi','DM Sans',sans-serif", transition: 'all 0.2s',
                            }}
                              onMouseEnter={e => { e.currentTarget.style.background = c.isActive ? 'var(--warning)' : '#2ECC71'; e.currentTarget.style.color = '#fff'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = c.isActive ? 'rgba(245,166,35,0.08)' : 'rgba(46,204,113,0.08)'; e.currentTarget.style.color = c.isActive ? 'var(--warning)' : '#2ECC71'; }}
                            >
                              {c.isActive ? <><LockIcon /> Lock</> : <><UnlockIcon /> Unlock</>}
                            </button>
                            <button onClick={() => handleEdit(c)} style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              padding: '7px 13px', borderRadius: 8,
                              border: '1.5px solid var(--border-strong)', background: 'transparent',
                              color: V2, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                              fontFamily: "'Satoshi','DM Sans',sans-serif", transition: 'all 0.2s',
                            }}
                              onMouseEnter={e => { e.currentTarget.style.background = V1; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = V1; }}
                              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = V2; e.currentTarget.style.borderColor = 'var(--border-strong)'; }}
                            >
                              <EditIcon /> Edit
                            </button>
                            <button onClick={() => handleDelete(c._id)} style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              padding: '7px 13px', borderRadius: 8,
                              border: '1.5px solid rgba(240,65,85,0.3)', background: 'rgba(240,65,85,0.08)',
                              color: 'var(--danger)', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                              fontFamily: "'Satoshi','DM Sans',sans-serif", transition: 'all 0.2s',
                            }}
                              onMouseEnter={e => { e.currentTarget.style.background = 'var(--danger)'; e.currentTarget.style.color = '#fff'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(240,65,85,0.08)'; e.currentTarget.style.color = 'var(--danger)'; }}
                            >
                              <TrashIcon /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 700px) { .form-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </div>
  );
};

export default AdminCourses;
